import _ from 'lodash';
import qs from 'qs';
import history from 'common/history';
import { DateTime } from 'common/utils/date';
import { csrfFromDom } from 'common/utils/server';
import api from 'qa/api';
import { startReview } from '../qaAssignments';

const anyAllocation = allocations =>
  _.get(
    _.find(allocations, a => a.type === 'any'),
    'count',
    ''
  );

const positiveFeedbackAllocation = allocations =>
  _.get(
    _.find(allocations, a => a.type === 'stars' && _.isEqual(a.value, [4, 5])),
    'count',
    ''
  );

const negativeFeedbackAllocation = allocations =>
  _.get(
    _.find(
      allocations,
      a => a.type === 'stars' && _.isEqual(a.value, [1, 2, 3])
    ),
    'count',
    ''
  );

const channelAllocations = allocations =>
  _.reduce(
    _.filter(allocations, a => a.type === 'channel'),
    (o, a) => {
      o[a.value] = { value: a.count };
      return o;
    },
    {}
  );

const tagAllocations = allocations =>
  _.reduce(
    _.filter(allocations, a => a.type === 'tags'),
    (o, a) => {
      o[a.value] = { name: a.value, value: a.count };
      return o;
    },
    {}
  );

// Actions

const UPDATE_NAME = 'qaAssignment/UPDATE_NAME';
const UPDATE_CADENCE = 'qaAssignment/UPDATE_CADENCE';
const UPDATE_INITIAL_START_DATE = 'qaAssignment/UPDATE_INITIAL_START_DATE';
const UPDATE_NUMBER_OF_DAYS = 'qaAssignment/UPDATE_NUMBER_OF_DAYS';
const UPDATE_DEFAULT_FORM_ID = 'qaAssignment/UPDATE_DEFAULT_FORM_ID';
const UPDATE_REPORTING_GROUP = 'qaAssignment/UPDATE_REPORTING_GROUP';
const UPDATE_QA_REVIEW_TYPE = 'qaAssignment/UPDATE_QA_REVIEW_TYPE';
const ADD_REVIEWER = 'qaAssignment/ADD_REVIEWER';
const REMOVE_REVIEWER = 'qaAssignment/REMOVE_REVIEWER';
export const ADD_GROUP = 'qaAssignment/ADD_GROUP';
const ADD_EMPLOYEE = 'qaAssignment/ADD_EMPLOYEE';
const REMOVE_GROUP = 'qaAssignment/REMOVE_GROUP';
export const REMOVE_EMPLOYEE = 'qaAssignment/REMOVE_EMPLOYEE';
const UPDATE_ALLOCATION = 'qaAssignment/UPDATE_ALLOCATION';
const FETCH_ASSIGNMENT_SUCCESS = 'qaAssignment/FETCH_ASSIGNMENT_SUCCESS';
export const UPDATE_ALLOCATE_TO_TEAM_LEADER =
  'qaAssignment/UPDATE_ALLOCATE_TO_TEAM_LEADER';
export const BUILD_CHANNEL_ALLOCATIONS =
  'qaAssignment/BUILD_CHANNEL_ALLOCATIONS';
const UPDATE_CHANNEL_ALLOCATION = 'qaAssignment/UPDATE_CHANNEL_ALLOCATION';
const UPDATE_TAG_ALLOCATION = 'qaAssignment/UPDATE_TAG_ALLOCATION';
const UPDATE_LOADING = 'qaAssignment/LOADING';
const FILTER_ADD_TAG = 'qaAssignment/FILTER_ADD_TAG';
const FILTER_UPDATE_TAG_CATEGORY = 'qaAssignment/FILTER_UPDATE_TAG_CATEGORY';
const FILTER_ADD_EXCLUDE_PROPERTY = 'qaAssignment/FILTER_ADD_EXCLUDE_PROPERTY';
export const FILTER_REMOVE_TAG = 'qaAssignment/FILTER_REMOVE_TAG';
export const FILTER_REMOVE_EXCLUDE_PROPERTY =
  'qaAssignment/FILTER_REMOVE_EXCLUDE_PROPERTY';
export const FILTER_UPDATE_LOOKBACK_DAYS =
  'qaAssignment/FILTER_UPDATE_LOOKBACK_DAYS';
const TOGGLE_BLIND = 'qaAssignment/TOGGLE_BLIND';
const TOGGLE_PARTIAL_MATCH = 'qaAssignment/TOGGLE_PARTIAL_MATCH';
const UPDATE_EMPLOYEES_AS_REVIEWEES = 'qaAssignment/EMPLOYEES_AS_REVIEWEES';
const CLEAR_GROUPS = 'qaAssignment/CLEAR_GROUPS';
const SET_ERRORS = 'qaAssignment/SET_ERRORS';
const CLEAR_EMPLOYEES = 'qaAssignment/CLEAR_EMPLOYEES';
const ADD_FILTER_VALUE = 'qaAssignment/ADD_FILTER_VALUE';
const REMOVE_FILTER_VALUE = 'qaAssignment/REMOVE_FILTER_VALUE';
const UPDATE_SPEECH_FILTER = 'qaAssignment/UPDATE_SPEECH_FILTER';
const UPDATE_FILTER_CATEGORY = 'qaAssignment/UPDATE_FILTER_CATEGORY';
const CLEAR_FORM = 'qaAssignment/CLEAR_FORM';
const ADD_REPORTING_GROUP = 'qaAssignment/ADD_REPORTING_GROUP';
const REMOVE_REPORTING_GROUP = 'qaAssignment/REMOVE_REPORTING_GROUP';
const CLEAR_REPORTING_GROUPS = 'qaAssignment/CLEAR_REPORTING_GROUPS';
const ADD_ROLE = 'qaAssignment/ADD_ROLE';
const REMOVE_ROLE = 'qaAssignment/REMOVE_ROLE';
const CLEAR_ROLES = 'qaAssignment/CLEAR_ROLES';
const ADD_EXCLUDED_EMPLOYEE = 'qaAssignment/ADD_EXCLUDED_EMPLOYEE';
const REMOVE_EXCLUDED_EMPLOYEE = 'qaAssignment/REMOVE_EXCLUDED_EMPLOYEE';
const CLEAR_EXCLUDED_EMPLOYEES = 'qaAssignment/CLEAR_EXCLUDED_EMPLOYEES';
const CLEAR_PARTICIPANTS_DATA = 'qaAssignment/CLEAR_PARTICIPANTS_DATA';

// Reducer

export const getInitialState = () => ({
  name: '',
  cadence: 'weekly',
  defaultFormId: null,
  reportingGroupId: null,
  reportingGroups: [],
  roles: [],
  excludedEmployees: [],
  allocateToTeamLeader: false,
  totalTodo: 0,
  progress: 0,
  totalReviewers: 0,
  totalEmployees: 0,
  skipped: 0,
  totalCompleted: 0,
  allotations: [],
  reviewers: [],
  progressByReviewer: [],
  progressByEmployee: [],
  groups: [],
  employees: [],
  // Allocations
  any: '',
  positiveFeedback: '',
  negativeFeedback: '',
  channels: {},
  tags: {},
  filters: {},
  blind: false,
  employeesAsReviewees: false,
  numberOfDays: null,
  initialStartDate: null,
  qaReviewType: 'qa_review',
  loading: false,
  errors: null,
});

export const initialState = getInitialState();

export function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case FETCH_ASSIGNMENT_SUCCESS: {
      const { assignment, duplicate } = action;
      const {
        id,
        cadence,
        progress,
        groups,
        excluded_employees: excludedEmployees,
        reporting_groups: reportingGroups,
        included_roles: roles,
        reporting_group_id: reportingGroupId,
        reporting_group_name: reportingGroupName,
        allocate_to_team_leader: allocateToTeamLeader,
        total_todo: totalTodo,
        total_reviewers: totalReviewers,
        total_employees: totalEmployees,
        total_completed: totalCompleted,
        qa_allocations: allocations,
        qa_review_type: qaReviewType,
        progress_by_reviewer: progressByReviewer,
        progress_by_employee: progressByEmployee,
        skipped,
        filters,
        blind,
        allow_best_match: allowPartialMatch,
        employees_as_reviewees: employeesAsReviewees,
        default_qa_form_id: defaultFormId,
        number_of_days: numberOfDays,
      } = assignment;
      let {
        name,
        assigned_employees: employees,
        assigned_reviewers: reviewers,
        initial_start_date: initialStartDate,
        start_date: startDate,
        end_date: endDate,
      } = assignment;
      if (duplicate) {
        name += ' [COPY]';
        if (!employeesAsReviewees) {
          employees = [];
        }
        if (allocateToTeamLeader) {
          reviewers = [];
        }
        initialStartDate =
          DateTime.fromISO(initialStartDate) > DateTime.fromISO(startDate)
            ? initialStartDate
            : startDate;
        startDate = null;
        endDate = null;
      }
      const any = anyAllocation(allocations);
      const positiveFeedback = positiveFeedbackAllocation(allocations);
      const negativeFeedback = negativeFeedbackAllocation(allocations);
      const channels = channelAllocations(allocations);
      const tags = tagAllocations(allocations);

      return {
        ...state,
        id,
        name,
        cadence,
        allocations,
        allocateToTeamLeader,
        totalTodo,
        totalReviewers,
        totalEmployees,
        totalCompleted,
        any,
        negativeFeedback,
        positiveFeedback,
        channels,
        tags,
        progress,
        progressByReviewer,
        progressByEmployee,
        groups,
        employees,
        reviewers,
        reportingGroupId,
        reportingGroups,
        reportingGroupName,
        qaReviewType,
        skipped,
        filters,
        blind,
        allowPartialMatch,
        employeesAsReviewees,
        defaultFormId,
        numberOfDays,
        excludedEmployees,
        startDate,
        initialStartDate,
        endDate,
        roles,
      };
    }
    case SET_ERRORS: {
      return { ...state, errors: action.errors };
    }
    case CLEAR_GROUPS: {
      return { ...state, groups: [], allocateToTeamLeader: false };
    }
    case CLEAR_FORM: {
      return { ...getInitialState() };
    }
    case CLEAR_EMPLOYEES: {
      return { ...state, employees: [] };
    }
    case UPDATE_EMPLOYEES_AS_REVIEWEES: {
      return { ...state, employeesAsReviewees: action.employeesAsReviewees };
    }
    case FILTER_UPDATE_LOOKBACK_DAYS: {
      const filters = { ...state.filters, lookback_days: action.lookbackDays };
      return { ...state, filters };
    }
    case FILTER_ADD_TAG: {
      const currentTags = state.filters[action.category] || [];
      const filters = {
        ...state.filters,
        [action.category]: _.union(currentTags, [action.tag]),
      };
      return { ...state, filters };
    }
    case FILTER_ADD_EXCLUDE_PROPERTY: {
      const excludeProperties = state.filters.exclude_properties || {};
      const currentValuesForKey = excludeProperties[action.key];
      const newValueForKey = action.value;

      if (_.isArray(newValueForKey)) {
        excludeProperties[action.key] = _.union(
          currentValuesForKey,
          newValueForKey
        );
      } else {
        excludeProperties[action.key] = {
          ...(currentValuesForKey || {}),
          ...newValueForKey,
        };
      }

      return {
        ...state,
        filters: {
          ...state.filters,
          exclude_properties: excludeProperties,
        },
      };
    }
    case FILTER_REMOVE_TAG: {
      const currentTags = state.filters[action.category] || [];
      const filters = {
        ...state.filters,
        [action.category]: _.filter(currentTags, t => t !== action.tag),
      };
      return { ...state, filters };
    }
    case FILTER_REMOVE_EXCLUDE_PROPERTY: {
      const current = state.filters.exclude_properties || [];
      const excludeProperties = {
        ...current,
        [action.key]: _.filter(current[action.key], t => t !== action.value),
      };
      const filters = {
        ...state.filters,
        exclude_properties: excludeProperties,
      };

      return { ...state, filters };
    }
    case FILTER_UPDATE_TAG_CATEGORY: {
      const { [action.prevCategory]: values = [], ...remainingFilters } =
        state.filters;
      return {
        ...state,
        filters: { ...remainingFilters, [action.category]: values },
      };
    }
    case ADD_FILTER_VALUE: {
      const currentValues =
        state.filters?.[action.category]?.[action.filterName] || [];
      const newFilters = {
        ...state.filters,
        [action.category]: {
          ...(state.filters?.[action.category] || {}),
          [action.filterName]: Array.from(
            new Set([...currentValues, action.value])
          ),
        },
      };
      return { ...state, filters: newFilters };
    }
    case REMOVE_FILTER_VALUE: {
      const currentValues =
        state.filters?.[action.category]?.[action.filterName] || [];
      const newFilters = {
        ...state.filters,
        [action.category]: {
          ...(state.filters?.[action.category] || {}),
          [action.filterName]: currentValues.filter(
            value => value !== action.value
          ),
        },
      };
      return { ...state, filters: newFilters };
    }
    case UPDATE_FILTER_CATEGORY: {
      const { [action.filterName]: values = [], ...cleanedPrevCategory } =
        state.filters?.[action.prevCategory] || {};
      const newFilters = {
        ...state.filters,
        [action.prevCategory]: {
          ...cleanedPrevCategory,
        },
        [action.category]: {
          ...(state.filters?.[action.category] || {}),
          [action.filterName]: values,
        },
      };
      return { ...state, filters: newFilters };
    }
    case UPDATE_SPEECH_FILTER: {
      const { [action.category]: categoryFilters, ...otherCategoriesFilters } =
        state.filters;

      const { [action.filterName]: prevFilterData, ...otherFilters } =
        categoryFilters || {};
      const newCategoryFilters = {
        ...otherFilters,
      };

      if (Object.values(action.value).length) {
        newCategoryFilters[action.filterName] = action.value;
      }

      const newFilters = {
        ...otherCategoriesFilters,
        [action.category]: {
          ...newCategoryFilters,
        },
      };
      return { ...state, filters: newFilters };
    }
    case UPDATE_LOADING: {
      return { ...state, loading: action.bool };
    }
    case UPDATE_NAME: {
      return { ...state, name: action.name, errors: null };
    }
    case UPDATE_CADENCE: {
      return { ...state, cadence: action.cadence };
    }
    case UPDATE_INITIAL_START_DATE: {
      return { ...state, initialStartDate: action.initialStartDate };
    }
    case UPDATE_NUMBER_OF_DAYS: {
      return { ...state, numberOfDays: action.numberOfDays };
    }
    case UPDATE_DEFAULT_FORM_ID: {
      return { ...state, defaultFormId: action.defaultFormId };
    }
    case UPDATE_REPORTING_GROUP: {
      return { ...state, reportingGroupId: action.reportingGroupId };
    }
    case UPDATE_QA_REVIEW_TYPE: {
      return { ...state, qaReviewType: action.qaReviewType };
    }
    case REMOVE_REPORTING_GROUP: {
      return {
        ...state,
        reportingGroups: _.filter(
          state.reportingGroups,
          ({ id }) => id != action.reportingGroup.id
        ),
      };
    }
    case ADD_REPORTING_GROUP: {
      const found = _.find(
        state.reportingGroups,
        el => el.id == action.reportingGroup.id
      );
      if (found) return state;

      return {
        ...state,
        reportingGroups: _.concat(state.reportingGroups, [
          action.reportingGroup,
        ]),
      };
    }
    case CLEAR_REPORTING_GROUPS: {
      return { ...state, reportingGroups: [] };
    }
    case REMOVE_ROLE: {
      return {
        ...state,
        roles: _.filter(state.roles, ({ id }) => id != action.role.id),
      };
    }
    case ADD_ROLE: {
      const found = state.roles.find(({ id }) => id == action.role.id);
      if (found) return state;

      return {
        ...state,
        roles: _.concat(state.roles, [action.role]),
      };
    }
    case CLEAR_ROLES: {
      return { ...state, roles: [] };
    }
    case REMOVE_EXCLUDED_EMPLOYEE: {
      return {
        ...state,
        excludedEmployees: _.filter(
          state.excludedEmployees,
          ({ id }) => id != action.excludedEmployee.id
        ),
      };
    }
    case ADD_EXCLUDED_EMPLOYEE: {
      const found = _.find(
        state.excludedEmployees,
        el => el.id == action.excludedEmployee.id
      );
      if (found) return state;

      return {
        ...state,
        excludedEmployees: _.concat(state.excludedEmployees, [
          action.excludedEmployee,
        ]),
      };
    }
    case CLEAR_EXCLUDED_EMPLOYEES: {
      return { ...state, excludedEmployees: [] };
    }
    case CLEAR_PARTICIPANTS_DATA: {
      return {
        ...state,
        roles: [],
        groups: [],
        employees: [],
        reviewers: [],
        reportingGroups: [],
        excludedEmployees: [],
      };
    }
    case REMOVE_REVIEWER: {
      return {
        ...state,
        reviewers: _.filter(state.reviewers, el => el.id != action.reviewer.id),
      };
    }
    case ADD_REVIEWER: {
      const found = _.find(state.reviewers, el => el.id == action.reviewer.id);
      if (found) return state;

      return {
        ...state,
        reviewers: _.concat(state.reviewers, [action.reviewer]),
      };
    }
    case REMOVE_GROUP: {
      return {
        ...state,
        groups: _.filter(state.groups, el => el.id != action.group.id),
      };
    }
    case REMOVE_EMPLOYEE: {
      return {
        ...state,
        employees: _.filter(state.employees, el => el.id != action.employee.id),
      };
    }
    case ADD_GROUP: {
      const found = _.find(state.groups, el => el.id == action.group.id);
      if (found) return state;

      const newGroups = _.concat(state.groups, [action.group]);
      let { reviewers } = state;

      if (state.allocateToTeamLeader) {
        reviewers = newGroups.map(({ owner }) => ({ id: Number(owner.id) }));
      }

      return {
        ...state,
        reviewers,
        groups: newGroups,
      };
    }
    case ADD_EMPLOYEE: {
      const found = _.find(state.employees, el => el.id == action.employee.id);
      if (found) return state;

      const newEmployees = _.concat(state.employees, [action.employee]);
      return {
        ...state,
        employees: newEmployees,
      };
    }
    case UPDATE_ALLOCATION: {
      return { ...state, [action.name]: action.value };
    }
    case UPDATE_CHANNEL_ALLOCATION: {
      const { slug, value } = action;
      const { name } = state.channels[slug];
      const channels = {
        ...state.channels,
        [slug]: { name, value },
      };
      return { ...state, channels };
    }
    case UPDATE_TAG_ALLOCATION: {
      const { name, value } = action;
      const tags = {
        ...state.tags,
        [name]: { name, value },
      };
      return { ...state, tags };
    }
    case TOGGLE_BLIND: {
      return { ...state, blind: !state.blind };
    }
    case TOGGLE_PARTIAL_MATCH: {
      return { ...state, allowPartialMatch: !state.allowPartialMatch };
    }
    case UPDATE_ALLOCATE_TO_TEAM_LEADER: {
      const allocateToTeamLeader = Boolean(action.allocateToTeamLeader);
      if (allocateToTeamLeader) {
        return {
          ...state,
          reviewers: action.groups.map(({ owner }) => ({
            id: Number(owner.id),
          })),
          allocateToTeamLeader,
        };
      }
      return { ...state, allocateToTeamLeader };
    }
    case BUILD_CHANNEL_ALLOCATIONS: {
      const newChannels = _.reduce(
        action.channels,
        (o, c) => {
          const { slug, name } = c;
          const current = state.channels[slug];
          if (current) {
            current.name = name;
            o[slug] = current;
          } else {
            o[slug] = { name, value: '' };
          }
          return o;
        },
        {}
      );
      const channels = { ...state.channels, ...newChannels };

      return { ...state, channels };
    }
    default: {
      return state;
    }
  }
}

// Action creators

export const clearParticipantsData = () => ({
  type: CLEAR_PARTICIPANTS_DATA,
});

export const clearGroups = () => ({
  type: CLEAR_GROUPS,
});

export const clearReportingGroups = () => ({
  type: CLEAR_REPORTING_GROUPS,
});

export const clearRoles = () => ({
  type: CLEAR_ROLES,
});

export const clearExcludedEmployees = () => ({
  type: CLEAR_EXCLUDED_EMPLOYEES,
});

export const clearForm = () => ({
  type: CLEAR_FORM,
});

export const setErrors = errors => ({
  type: SET_ERRORS,
  errors,
});

export const clearEmployees = () => ({
  type: CLEAR_EMPLOYEES,
});

export const updateEmployeesAsReviewees = employeesAsReviewees => ({
  type: UPDATE_EMPLOYEES_AS_REVIEWEES,
  employeesAsReviewees,
});

export const filterUpdateLookbackDays = lookbackDays => ({
  type: FILTER_UPDATE_LOOKBACK_DAYS,
  lookbackDays,
});

export const filterAddTag = (tag, category = 'exclude_tags') => ({
  type: FILTER_ADD_TAG,
  category,
  tag,
});

export const filterAddExcludeProperty = (key, value) => ({
  type: FILTER_ADD_EXCLUDE_PROPERTY,
  key,
  value,
});

export const filterRemoveTag = (tag, category = 'exclude_tags') => ({
  type: FILTER_REMOVE_TAG,
  category,
  tag,
});

export const filterUpdateTagCategory = (category, prevCategory) => ({
  type: FILTER_UPDATE_TAG_CATEGORY,
  category,
  prevCategory,
});

export const filterRemoveExcludeProperty = (key, value) => ({
  type: FILTER_REMOVE_EXCLUDE_PROPERTY,
  key,
  value,
});

export const updateTagAllocation = (name, value) => ({
  type: UPDATE_TAG_ALLOCATION,
  name,
  value,
});

export const updateChannelAllocation = (slug, value) => ({
  type: UPDATE_CHANNEL_ALLOCATION,
  slug,
  value,
});

export const updateAllocateToTeamLeader = (
  allocateToTeamLeader,
  groups = []
) => ({
  type: UPDATE_ALLOCATE_TO_TEAM_LEADER,
  groups,
  allocateToTeamLeader,
});

export const updateName = name => ({
  type: UPDATE_NAME,
  name,
});

export const updateCadence = cadence => ({
  type: UPDATE_CADENCE,
  cadence,
});

export const updateInitialStartDate = initialStartDate => ({
  type: UPDATE_INITIAL_START_DATE,
  initialStartDate,
});

export const updateNumberOfDays = numberOfDays => ({
  type: UPDATE_NUMBER_OF_DAYS,
  numberOfDays,
});

export const updateDefaultFormId = defaultFormId => ({
  type: UPDATE_DEFAULT_FORM_ID,
  defaultFormId,
});

export const updateReportingGroup = reportingGroupId => ({
  type: UPDATE_REPORTING_GROUP,
  reportingGroupId,
});

export const updateQaReviewType = qaReviewType => ({
  type: UPDATE_QA_REVIEW_TYPE,
  qaReviewType,
});

export const addReviewer = reviewer => ({
  type: ADD_REVIEWER,
  reviewer,
});

export const removeReviewer = reviewer => ({
  type: REMOVE_REVIEWER,
  reviewer,
});

export const addGroup = group => ({
  type: ADD_GROUP,
  group,
});

export const removeGroup = group => ({
  type: REMOVE_GROUP,
  group,
});

export const addEmployee = employee => ({
  type: ADD_EMPLOYEE,
  employee,
});

export const removeEmployee = employee => ({
  type: REMOVE_EMPLOYEE,
  employee,
});

export const addReportingGroup = reportingGroup => ({
  type: ADD_REPORTING_GROUP,
  reportingGroup,
});

export const removeReportingGroup = reportingGroup => ({
  type: REMOVE_REPORTING_GROUP,
  reportingGroup,
});

export const addExcludedEmployee = excludedEmployee => ({
  type: ADD_EXCLUDED_EMPLOYEE,
  excludedEmployee,
});

export const removeExcludedEmployee = excludedEmployee => ({
  type: REMOVE_EXCLUDED_EMPLOYEE,
  excludedEmployee,
});

export const addRole = role => ({
  type: ADD_ROLE,
  role,
});

export const removeRole = role => ({
  type: REMOVE_ROLE,
  role,
});

export const updateAllocation = (name, value) => ({
  type: UPDATE_ALLOCATION,
  name,
  value,
});

const fetchAssignmentSuccess = (assignment, duplicate = false) => ({
  type: FETCH_ASSIGNMENT_SUCCESS,
  assignment,
  duplicate,
});

export const buildChannelAllocations = channels => ({
  type: BUILD_CHANNEL_ALLOCATIONS,
  channels,
});

export const updateLoading = bool => ({
  type: UPDATE_LOADING,
  bool,
});

export const toggleBlind = () => ({
  type: TOGGLE_BLIND,
});

export const togglePartialMatch = () => ({
  type: TOGGLE_PARTIAL_MATCH,
});

export const addFilterValue = (category, filterName, value) => ({
  type: ADD_FILTER_VALUE,
  category,
  filterName,
  value,
});

export const removeFilterValue = (category, filterName, value) => ({
  type: REMOVE_FILTER_VALUE,
  category,
  filterName,
  value,
});

export const updateFilterCategory = (category, prevCategory, filterName) => ({
  type: UPDATE_FILTER_CATEGORY,
  category,
  prevCategory,
  filterName,
});

export const updateSpeechFilters = (category, filterName, value) => ({
  type: UPDATE_SPEECH_FILTER,
  category,
  filterName,
  value,
});

// Thunks

export const getAssignment = (id, startDate, endDate) => dispatch => {
  let params = '';
  if (startDate && endDate) {
    params = qs.stringify({ start_date: startDate, end_date: endDate });
  }
  return api.fetch(dispatch, `qa_assignments/${id}?${params}`);
};

export const fetchAssignment =
  ({ id, startDate, endDate, duplicate }) =>
    dispatch => {
      dispatch(updateLoading(true));
      return dispatch(getAssignment(id, startDate, endDate)).then(([json]) => {
        dispatch(fetchAssignmentSuccess(json, duplicate));
        dispatch(updateLoading(false));
      });
    };

export const getAssignmentInput = (assignment, duplicate) => {
  const {
    name,
    cadence,
    initialStartDate,
    numberOfDays,
    reviewers,
    groups,
    employees,
    any,
    positiveFeedback,
    negativeFeedback,
    allocateToTeamLeader,
    channels,
    tags,
    filters,
    blind,
    allowPartialMatch,
    reportingGroupId,
    reportingGroups,
    excludedEmployees,
    roles,
    defaultFormId,
    qaReviewType,
  } = assignment;

  const allocations = _.filter(
    [
      { type: 'any', value: null, count: any },
      { type: 'stars', value: [4, 5], count: positiveFeedback },
      { type: 'stars', value: [1, 2, 3], count: negativeFeedback },
    ],
    o => o.count > 0
  );
  const channelAllocations = _.map(channels, ({ value }, slug) => ({
    type: 'channel',
    value: slug,
    count: value,
  }));

  const tagAllocations = _.map(tags, ({ name, value }) => ({
    type: 'tags',
    value: name,
    count: value,
  }));

  const groupIds = _.map(groups, 'id');
  const reportingGroupsIds = _.map(reportingGroups, 'id');
  const excludedEmployeesIds = _.map(excludedEmployees, 'id');
  const roleIds = _.map(roles, 'id');
  const employeeIds =
    duplicate && groupIds.length > 0 ? [] : _.map(employees, 'id');

  const assignmentInput = {
    name,
    cadence,
    initial_start_date: initialStartDate,
    number_of_days: numberOfDays,
    reviewers: _.map(reviewers, 'id'),
    groups: groupIds,
    employees: employeeIds,
    qa_allocations: _.concat(allocations, channelAllocations, tagAllocations),
    allocate_to_team_leader: allocateToTeamLeader,
    filters,
    blind,
    qa_review_type: qaReviewType,
    allow_best_match: allowPartialMatch,
    reporting_group_id: reportingGroupId,
    excluded_employees: excludedEmployeesIds,
    ...(reportingGroupsIds.length
      ? { reporting_groups: reportingGroupsIds }
      : {}),
    ...(roleIds.length ? { included_roles: roleIds } : {}),
    ...(defaultFormId ? { default_qa_form_id: defaultFormId } : {}),
  };

  return assignmentInput;
};

export const saveAssignment =
  (duplicate = false) =>
    (dispatch, getState) => {
      const assignmentInput = getAssignmentInput(
        getState().qaAssignment,
        duplicate
      );
      const body = JSON.stringify(assignmentInput);

      return api
        .fetch(dispatch, 'qa_assignments', {
          method: 'POST',
          credentials: 'include',
          headers: { 'X-CSRF-token': csrfFromDom() },
          body,
        })
        .then(data => {
          if (data?.errors) {
            dispatch(setErrors(data.errors));
          } else {
            dispatch(setErrors());
            history.push('/qa/assignments');
          }
        });
    };

export const archiveAssignment = id => dispatch =>
  api.fetch(dispatch, `qa_assignments/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'X-CSRF-token': csrfFromDom() },
  });

export const skip =
  (
    qaAssignmentId,
    solicitationId,
    defaultFormId = null,
    employeeId,
    reviewSimilar = false,
    reasonId,
    reasonDetail,
    allocationId
  ) =>
    dispatch =>
      api
        .fetch(dispatch, `qa_assignment_skips`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'X-CSRF-token': csrfFromDom() },
          body: JSON.stringify({
            uuid: solicitationId,
            assignment_id: qaAssignmentId,
            reason_id: reasonId,
            reason_detail: reasonDetail,
            qa_allocation_id: allocationId,
          }),
        })
        .then(async () => {
          const foundReview = await dispatch(
            startReview(qaAssignmentId, defaultFormId, employeeId)
          );
          const reviewSimilarURL = `/qa/assignments/${qaAssignmentId}/review_similar`;
          if (reviewSimilar && !foundReview) {
            const foundSimilarReview = await dispatch(
              startReview(qaAssignmentId, defaultFormId, employeeId, true)
            );
            if (!foundSimilarReview) {
              history.push(reviewSimilarURL);
            }
          } else if (!foundReview) {
            history.push(reviewSimilarURL);
          }
        });
